import { ExerciseImage } from 'components/ExerciseImage';
import { StaticSetIndicator } from 'components/StaticSetIndicator';
import { SupersetChicklet } from 'components/SupersetChicklet';
import React from 'react';
import styled from 'styled-components';
import { colors } from 'styleguide/colors';
import { TextLG, TextMD, TextSMMedium } from 'styleguide/Texts';
import { themeState } from 'styleguide/theme';
import { View } from 'styleguide/View';
import { ExerciseProps } from './types';
import { SetPersonalRecordType } from 'hevy-shared';
import { Icon } from 'styleguide/Icon';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;

const Column1 = styled.div`
  display: flex;
  flex: 1;
  justify-content: left;
  padding-left: 16px;
`;

const Column2 = styled.div`
  display: flex;
  flex: 4;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const SetContainer = styled.div`
  display: flex;
  padding: 8px 0;
`;

const prTitleForPRType = (prType: SetPersonalRecordType) => {
  switch (prType) {
    case 'best_1rm':
      return 'Best 1 Rep Max';
    case 'best_distance':
      return 'Best Distance';
    case 'best_duration':
      return 'Best Duration';
    case 'best_volume':
      return 'Best Volume';
    case 'best_weight':
      return 'Best Weight';
    case 'best_reps':
      return 'Best Reps';
  }
};

export const Exercise = ({
  supersetId,
  image,
  title,
  notes,
  secondColumnHeader,
  sets,
  onExerciseClick,
}: ExerciseProps) => {
  return (
    <Container>
      {supersetId !== undefined && (
        <View style={{ flexDirection: 'row', marginBottom: 16 }}>
          <SupersetChicklet id={supersetId} />
        </View>
      )}
      <TitleContainer
        onClick={() => {
          if (onExerciseClick) onExerciseClick();
        }}
        style={{ cursor: onExerciseClick ? 'pointer' : 'default' }}
      >
        <ExerciseImage source={image} diameter={40} />
        <TextLG style={{ marginLeft: 16 }}>{title}</TextLG>
      </TitleContainer>
      {!!notes && (
        <TextMD type="secondary" style={{ marginTop: 8 }}>
          {notes}
        </TextMD>
      )}
      <HeaderContainer>
        <Column1>
          <TextSMMedium type="secondary">SETS</TextSMMedium>
        </Column1>
        <Column2>
          <TextSMMedium type="secondary">{secondColumnHeader}</TextSMMedium>
        </Column2>
      </HeaderContainer>
      {sets.map((s, index) => (
        <SetContainer
          key={s.key}
          style={{ backgroundColor: getSimpleSetBackgroundColor(index, themeState.current.name) }}
        >
          <Column1>
            <StaticSetIndicator indicator={s.indicator} />
          </Column1>
          <Column2>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <TextMD>{s.value}</TextMD>
              {s.rpe && <TextMD type="secondary">&nbsp;@ {s.rpe} rpe</TextMD>}
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16,
                paddingRight: 16,
                flexWrap: 'wrap',
              }}
            >
              {s.prs.map((pr, index) => {
                return (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }} key={index}>
                    <Icon type="pr" style={{ height: 16, width: 16, marginRight: 4 }}></Icon>
                    <TextSMMedium style={{ color: colors.neutral400 }}>
                      {prTitleForPRType(pr.type)}
                    </TextSMMedium>
                  </View>
                );
              })}
            </View>
          </Column2>
        </SetContainer>
      ))}
    </Container>
  );
};

const getSimpleSetBackgroundColor = (index: number, theme: 'dark' | 'light'): string => {
  const oddSetColor = theme === 'light' ? colors.neutral100 : colors.neutral700;

  return index % 2 === 0 ? colors.transparent : oddSetColor;
};
